import React, { useState } from 'react';
import { motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

const { FiX, FiAlertTriangle, FiCheck, FiPlusCircle } = FiIcons;

const CATEGORIES = ['Protein', 'Carbohydrate', 'Fat', 'Vegetable'];
const DEFAULT_SERVING_OPTIONS = ['g', 'oz', 'cup'];

const CustomFoodForm = ({ onClose, onAddFood, nextId }) => {
  const [foodData, setFoodData] = useState({
    name: '',
    category: 'Protein',
    calories: '',
    protein: '',
    carbs: '',
    fat: '',
    servingSize: 100,
    servingUnit: 'g',
    servingOptions: [...DEFAULT_SERVING_OPTIONS],
    customServingOption: ''
  });
  const [error, setError] = useState('');
  const [calorieWarning, setCalorieWarning] = useState(null);
  const [showCalorieWarningModal, setShowCalorieWarningModal] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFoodData(prev => ({ ...prev, [name]: name === 'calories' || name === 'protein' || name === 'carbs' || name === 'fat' || name === 'servingSize' ? parseFloat(value) || '' : value }));
  };

  const addCustomServingOption = () => {
    if (!foodData.customServingOption.trim()) return;
    
    if (foodData.servingOptions.includes(foodData.customServingOption)) {
      setError('This serving option already exists');
      return;
    }
    
    setFoodData(prev => ({
      ...prev,
      servingOptions: [...prev.servingOptions, prev.customServingOption],
      customServingOption: ''
    }));
    setError('');
  };

  const removeServingOption = (option) => {
    if (option === foodData.servingUnit) {
      setError('Cannot remove the selected serving unit');
      return;
    }
    
    setFoodData(prev => ({
      ...prev,
      servingOptions: prev.servingOptions.filter(opt => opt !== option)
    }));
  };

  const validateForm = () => {
    if (!foodData.name.trim()) {
      setError('Please enter a food name');
      return false;
    }
    
    if (!foodData.calories || foodData.calories <= 0) {
      setError('Please enter valid calories');
      return false;
    }
    
    if (!foodData.protein || foodData.protein < 0) {
      setError('Please enter valid protein amount');
      return false;
    }
    
    if (!foodData.carbs || foodData.carbs < 0) {
      setError('Please enter valid carbohydrates amount');
      return false;
    }
    
    if (!foodData.fat || foodData.fat < 0) {
      setError('Please enter valid fat amount');
      return false;
    }
    
    if (!foodData.servingSize || foodData.servingSize <= 0) {
      setError('Please enter valid serving size');
      return false;
    }
    
    // Check for calorie discrepancy
    const calculatedCalories = (foodData.protein * 4) + (foodData.carbs * 4) + (foodData.fat * 9);
    const calorieDiscrepancy = Math.abs(calculatedCalories - foodData.calories);
    const discrepancyPercentage = (calorieDiscrepancy / foodData.calories) * 100;
    
    if (discrepancyPercentage > 20) {
      const warning = {
        enteredCalories: foodData.calories,
        calculatedCalories: calculatedCalories,
        discrepancyPercentage: Math.round(discrepancyPercentage)
      };
      setCalorieWarning(warning);
      setShowCalorieWarningModal(true);
      return false;
    }
    
    return true;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    setError('');
    
    if (validateForm()) {
      const newFood = {
        id: nextId,
        name: foodData.name,
        category: foodData.category,
        calories: parseFloat(foodData.calories),
        protein: parseFloat(foodData.protein),
        carbs: parseFloat(foodData.carbs),
        fat: parseFloat(foodData.fat),
        servingSize: parseFloat(foodData.servingSize),
        servingUnit: foodData.servingUnit,
        servingOptions: foodData.servingOptions,
        isCustom: true
      };
      
      onAddFood(newFood);
    }
  };

  const handleAddAnyway = () => {
    const newFood = {
      id: nextId,
      name: foodData.name,
      category: foodData.category,
      calories: parseFloat(foodData.calories),
      protein: parseFloat(foodData.protein),
      carbs: parseFloat(foodData.carbs),
      fat: parseFloat(foodData.fat),
      servingSize: parseFloat(foodData.servingSize),
      servingUnit: foodData.servingUnit,
      servingOptions: foodData.servingOptions,
      isCustom: true
    };
    
    onAddFood(newFood);
    setShowCalorieWarningModal(false);
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50"
    >
      <motion.div
        initial={{ scale: 0.9, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.9, opacity: 0 }}
        className="bg-white rounded-2xl shadow-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto"
      >
        <div className="p-6 border-b border-gray-200 flex justify-between items-center">
          <h2 className="text-2xl font-bold text-gray-800 flex items-center gap-2">
            <SafeIcon icon={FiPlusCircle} className="text-indigo-600" />
            Add Custom Food
          </h2>
          <button
            onClick={onClose}
            className="text-gray-500 hover:text-gray-700 p-1 rounded-full hover:bg-gray-100"
          >
            <SafeIcon icon={FiX} className="text-xl" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {error && (
            <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg flex items-center gap-2">
              <SafeIcon icon={FiAlertTriangle} className="text-red-500" />
              <span>{error}</span>
            </div>
          )}

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Food Name */}
            <div className="col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Food Name
              </label>
              <input
                type="text"
                name="name"
                value={foodData.name}
                onChange={handleChange}
                placeholder="e.g. Pinto Beans"
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
              />
            </div>

            {/* Category */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Category
              </label>
              <select
                name="category"
                value={foodData.category}
                onChange={handleChange}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
              >
                {CATEGORIES.map(category => (
                  <option key={category} value={category}>{category}</option>
                ))}
              </select>
            </div>

            {/* Serving Size & Unit */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Serving Size
                </label>
                <input
                  type="number"
                  name="servingSize"
                  value={foodData.servingSize}
                  onChange={handleChange}
                  min="0"
                  step="0.1"
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Serving Unit
                </label>
                <select
                  name="servingUnit"
                  value={foodData.servingUnit}
                  onChange={handleChange}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                >
                  {foodData.servingOptions.map(option => (
                    <option key={option} value={option}>{option}</option>
                  ))}
                </select>
              </div>
            </div>

            {/* Serving Options */}
            <div className="col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Serving Options
              </label>
              <div className="flex flex-wrap gap-2 mb-2">
                {foodData.servingOptions.map(option => (
                  <div 
                    key={option} 
                    className={`px-3 py-1 rounded-full text-sm flex items-center gap-1
                      ${option === foodData.servingUnit 
                        ? 'bg-indigo-100 text-indigo-800 border border-indigo-300' 
                        : 'bg-gray-100 text-gray-700'
                      }`}
                  >
                    <span>{option}</span>
                    {option !== foodData.servingUnit && (
                      <button
                        type="button"
                        onClick={() => removeServingOption(option)}
                        className="text-gray-500 hover:text-gray-700"
                      >
                        <SafeIcon icon={FiX} className="text-xs" />
                      </button>
                    )}
                  </div>
                ))}
              </div>
              <div className="flex gap-2">
                <input
                  type="text"
                  name="customServingOption"
                  value={foodData.customServingOption}
                  onChange={handleChange}
                  placeholder="Add serving option"
                  className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                />
                <button
                  type="button"
                  onClick={addCustomServingOption}
                  className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors"
                >
                  Add
                </button>
              </div>
            </div>

            {/* Nutritional Values */}
            <div className="col-span-2">
              <h3 className="text-lg font-medium text-gray-800 mb-3">Nutritional Values</h3>
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Calories
                  </label>
                  <div className="relative">
                    <input
                      type="number"
                      name="calories"
                      value={foodData.calories}
                      onChange={handleChange}
                      min="0"
                      step="0.1"
                      className="w-full px-4 py-2 pr-12 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                    />
                    <span className="absolute right-3 top-2 text-gray-500">kcal</span>
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Protein
                  </label>
                  <div className="relative">
                    <input
                      type="number"
                      name="protein"
                      value={foodData.protein}
                      onChange={handleChange}
                      min="0"
                      step="0.1"
                      className="w-full px-4 py-2 pr-8 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                    />
                    <span className="absolute right-3 top-2 text-gray-500">g</span>
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Carbs
                  </label>
                  <div className="relative">
                    <input
                      type="number"
                      name="carbs"
                      value={foodData.carbs}
                      onChange={handleChange}
                      min="0"
                      step="0.1"
                      className="w-full px-4 py-2 pr-8 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                    />
                    <span className="absolute right-3 top-2 text-gray-500">g</span>
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Fat
                  </label>
                  <div className="relative">
                    <input
                      type="number"
                      name="fat"
                      value={foodData.fat}
                      onChange={handleChange}
                      min="0"
                      step="0.1"
                      className="w-full px-4 py-2 pr-8 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                    />
                    <span className="absolute right-3 top-2 text-gray-500">g</span>
                  </div>
                </div>
              </div>

              <div className="mt-2 text-sm text-gray-500">
                <span>* Based on serving size of {foodData.servingSize} {foodData.servingUnit}</span>
              </div>
            </div>
          </div>

          <div className="flex justify-end pt-4 border-t border-gray-200">
            <div className="flex gap-3">
              <button
                type="button"
                onClick={onClose}
                className="px-6 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors"
              >
                Cancel
              </button>
              <button
                type="submit"
                className="px-6 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors flex items-center gap-2"
              >
                <SafeIcon icon={FiCheck} />
                Add Food
              </button>
            </div>
          </div>
        </form>
      </motion.div>

      {/* Calorie Warning Modal */}
      {showCalorieWarningModal && calorieWarning && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-[60]"
          onClick={() => setShowCalorieWarningModal(false)}
        >
          <motion.div
            initial={{ scale: 0.9, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.9, opacity: 0 }}
            className="bg-white rounded-2xl shadow-xl w-full max-w-md p-6"
            onClick={e => e.stopPropagation()}
          >
            <div className="text-center mb-4">
              <SafeIcon icon={FiAlertTriangle} className="text-5xl text-orange-500 mx-auto mb-2" />
              <h3 className="text-xl font-bold text-gray-800">Calorie Discrepancy Warning</h3>
            </div>

            <p className="text-gray-600 mb-4">
              The {Math.round(calorieWarning.enteredCalories)} calories entered differ from the {Math.round(calorieWarning.calculatedCalories)} calories 
              represented by the proteins, carbs, and fats by more than {calorieWarning.discrepancyPercentage}%.
            </p>

            <div className="flex justify-center gap-4 mt-6">
              <button
                onClick={() => setShowCalorieWarningModal(false)}
                className="px-6 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors"
              >
                Edit Food
              </button>
              <button
                onClick={handleAddAnyway}
                className="px-6 py-2 bg-orange-500 text-white rounded-lg hover:bg-orange-600 transition-colors"
              >
                Add Anyway
              </button>
            </div>
          </motion.div>
        </motion.div>
      )}
    </motion.div>
  );
};

export default CustomFoodForm;